import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import {
    OrganizationTaskTemplatesView,
    OrganizationTaskTemplatesViewColumn,
    OrganizationTaskTemplatesViewSchema,
} from "@common/types/views/organizationTaskTemplatesView";
import { View } from "@common/types/views/views";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { ensureViewColumn, mergeCachedView } from "@lib/mergeCachedView";
import { organizationTaskTemplatesViewConfig } from "./organizationTaskTemplatesViewConfig";

/**
 * Saved layouts that predate the Service template column get it added once,
 * so existing users see it without resetting their settings. Removing it
 * afterwards sticks.
 */
export function useOrganizationTaskTemplatesView(
    defaultView: OrganizationTaskTemplatesView,
) {
    const [cachedView, setCachedView] =
        useWorkspaceUserCache<OrganizationTaskTemplatesView>(
            WORKSPACE_USER_CACHE_KEYS.ORGANIZATION_TASK_TEMPLATES_PAGE_VIEW_CACHE_KEY,
            OrganizationTaskTemplatesViewSchema,
        );
    const [wasServiceTemplatesColumnOffered, setServiceTemplatesColumnOffered] =
        useWorkspaceUserCache<boolean>(
            WORKSPACE_USER_CACHE_KEYS.ORGANIZATION_TASK_TEMPLATES_SERVICE_TEMPLATES_COLUMN_OFFERED_CACHE_KEY,
            z.boolean(),
        );
    const isColumnOfferPending =
        cachedView !== undefined && wasServiceTemplatesColumnOffered !== true;
    const isColumnOfferPendingRef = useRef(isColumnOfferPending);
    const [view, setView] = useState<OrganizationTaskTemplatesView>(() => {
        const mergedView = mergeCachedView(
            cachedView,
            defaultView,
            organizationTaskTemplatesViewConfig,
        );
        return isColumnOfferPending
            ? ensureViewColumn(
                  mergedView,
                  OrganizationTaskTemplatesViewColumn.SERVICE_TEMPLATES,
              )
            : mergedView;
    });

    useEffect(() => {
        if (!isColumnOfferPendingRef.current) {
            return;
        }
        isColumnOfferPendingRef.current = false;
        setCachedView(view);
        setServiceTemplatesColumnOffered(true);
    }, [view, setCachedView, setServiceTemplatesColumnOffered]);

    const handleViewChange = useCallback(
        (newView: View) => {
            const parsedView =
                OrganizationTaskTemplatesViewSchema.parse(newView);
            setView(parsedView);
            setCachedView(parsedView);
            setServiceTemplatesColumnOffered(true);
        },
        [setCachedView, setServiceTemplatesColumnOffered],
    );

    return { view, handleViewChange };
}
